import './styles.css'
import Papa from 'papaparse'
import { Graph } from '@cosmos.gl/graph'

import {
  DEFAULT_FILTER_MIN,
  DESKTOP_PIXEL_RATIO,
  EDGE_ALPHA_BASE,
  EDGE_ALPHA_RANGE,
  FIT_VIEW_DELAY_MS,
  LINK_GREYOUT_OPACITY,
  LINK_VIS_DISTANCE_RANGE,
  LINK_VIS_MIN_TRANSPARENCY,
  MAX_SEARCH_RESULTS,
  MIN_ZOOM,
  MOBILE_BREAKPOINT,
  PLAYER_FOCUS_MS,
  PLAYER_FOCUS_ZOOM,
  POINT_GREYOUT_OPACITY,
  RECENTER_MS,
  SEARCH_BLUR_MS,
  SELECT_PLAYER_TIP_X_RATIO,
  SELECT_PLAYER_TIP_Y_RATIO,
  TEAM_IMAGE_RES,
  TEAM_STROKE_RATIO,
  TOOLTIP_PAD,
  TOUCH_PAUSE_DELAY_MS,
  TOUCH_PIXEL_RATIO,
  TOUCH_QUERY,
  TOUCH_TOOLTIP_LIFT,
} from './constants'
import { Edge, loadData, leagueDisplayName, teamPalette } from './data'

const {
  rawNodes, nodeLabels, pointSizes, pointColors, pointPositions,
  edges, maxGames, minPlayerGames, maxPlayerGames,
  indicesByTeam, allTeams,
  sortedLeagues, teamsByLeague,
  fullTeamName, teamYears, playerPasses,
} = await loadData()

// ── DOM refs ───────────────────────────────────────────────────────────────

const graphDiv         = document.querySelector<HTMLDivElement>('#graph')!
const tooltip          = document.getElementById('tooltip')!
const recenterBtn      = document.getElementById('recenter') as HTMLButtonElement
const simToggle        = document.getElementById('sim-toggle') as HTMLButtonElement
const simToggleFloat   = document.getElementById('sim-toggle-float') as HTMLButtonElement
const recenterFloat    = document.getElementById('recenter-float') as HTMLButtonElement
const simSettingsBtn   = document.getElementById('sim-settings-toggle') as HTMLButtonElement
const simSettingsPanel = document.getElementById('sim-settings') as HTMLElement
const simCloseBtn      = document.getElementById('sim-close') as HTMLButtonElement
const helpToggle       = document.getElementById('help-toggle') as HTMLButtonElement
const helpPanel        = document.getElementById('help-panel') as HTMLElement
const helpCloseBtn     = document.getElementById('help-close') as HTMLButtonElement
const searchInput      = document.getElementById('search') as HTMLInputElement
const searchResults    = document.getElementById('search-results') as HTMLUListElement
const teamList         = document.getElementById('team-list') as HTMLUListElement
const teamsClear       = document.getElementById('teams-clear') as HTMLButtonElement
const teamsAll         = document.getElementById('teams-all') as HTMLButtonElement
const edgeCutoffInput  = document.getElementById('edge-cutoff') as HTMLInputElement
const edgeCutoffVal    = document.getElementById('cutoff-val') as HTMLInputElement
const playerCutoffInput = document.getElementById('player-cutoff') as HTMLInputElement
const playerCutoffVal   = document.getElementById('player-cutoff-val') as HTMLInputElement
const panelToggle       = document.getElementById('panel-toggle') as HTMLButtonElement
const pairTipA          = document.getElementById('pair-tip-a') as HTMLElement
const pairTipB          = document.getElementById('pair-tip-b') as HTMLElement
const pairEdgeLabel     = document.getElementById('pair-edge-label') as HTMLElement

const isTouch = window.matchMedia(TOUCH_QUERY).matches

// ── State ──────────────────────────────────────────────────────────────────

let lockedIndex: number | null = null
let lockedHighlighted = new Set<number>()
let pairIndex: number | null = null
let pairRafId: number | null = null

// src<<20 | tgt keyed lookup (src < tgt) for O(1) edge-weight access in pair mode.
const edgeWeights = new Map<number, number>()
const pairKey = (a: number, b: number) => (a < b ? a : b) * (1 << 20) + (a < b ? b : a)
const selectedTeams = new Set<string>(allTeams)  // all teams = no filter active

let activeLinks = new Float32Array(0)
let activeLinkColors = new Float32Array(0)
let connectedSet = new Set<number>()
// [edgeCutoff, playerCutoff] last committed, or null if never committed.
let lastCutoffs: [number, number] | null = null

// Per-node display sizes for the team image layer. Mirrors pointSizes except
// orphan-filtered nodes get 0 to hide the image.
const pointImageSizes = new Float32Array(pointSizes)

// Tracked by onPointMouseOver so a capture-phase pointerdown handler can
// decide whether the upcoming drag should be cancelled.
let lastHoveredIndex: number | null = null

for (const e of edges) edgeWeights.set(pairKey(e.src, e.tgt), e.gamesPlayed)

// ── Filter pipeline ────────────────────────────────────────────────────────

// Rebuild activeLinks / activeLinkColors / connectedSet from the two cutoffs.
// Pure in-memory work; graph.set* is the caller's responsibility.
function rebuildActive(edgeCutoff: number, playerCutoff: number) {
  const filtered: Edge[] = []
  for (const e of edges) {
    if (e.gamesPlayed < edgeCutoff) continue
    if (!playerPasses(e.src, playerCutoff) || !playerPasses(e.tgt, playerCutoff)) continue
    filtered.push(e)
  }
  activeLinks = new Float32Array(filtered.length * 2)
  activeLinkColors = new Float32Array(filtered.length * 4)
  connectedSet = new Set<number>()
  const localMax = filtered.reduce((m, e) => Math.max(m, e.gamesPlayed), 1)
  filtered.forEach((e, i) => {
    activeLinks[i * 2]     = e.src
    activeLinks[i * 2 + 1] = e.tgt
    connectedSet.add(e.src)
    connectedSet.add(e.tgt)
    const alpha = EDGE_ALPHA_BASE + (e.gamesPlayed / localMax) * EDGE_ALPHA_RANGE
    activeLinkColors[i * 4]     = 1
    activeLinkColors[i * 4 + 1] = 1
    activeLinkColors[i * 4 + 2] = 1
    activeLinkColors[i * 4 + 3] = alpha
  })
}

// Cheap live preview during slider drag — no orphan masking, no lock reset.
function previewFilters() {
  tooltip.hidden = true
  rebuildActive(currentEdge(), currentPlayer())
  graph.setLinks(activeLinks)
  graph.setLinkColors(activeLinkColors)
  graph.render()
}

// Full commit on slider release: reset lock state, rebuild, mask orphans,
// sync the graph once, render once.
function commitFilters(edgeCutoff: number, playerCutoff: number) {
  if (lastCutoffs && lastCutoffs[0] === edgeCutoff && lastCutoffs[1] === playerCutoff) return
  lastCutoffs = [edgeCutoff, playerCutoff]

  resetLockState()
  rebuildActive(edgeCutoff, playerCutoff)

  const maskedSizes = new Float32Array(pointSizes)
  const maskedColors = new Float32Array(pointColors)
  const maskedImageSizes = new Float32Array(pointImageSizes)
  const maskedImageIndices = new Float32Array(pointImageIndices)
  for (let i = 0; i < rawNodes.length; i++) {
    if (!connectedSet.has(i)) {
      maskedSizes[i] = 0
      maskedColors[i * 4 + 3] = 0
      maskedImageSizes[i] = 0
      maskedImageIndices[i] = hiddenImageIndex
    }
  }
  graph.setLinks(activeLinks)
  graph.setLinkColors(activeLinkColors)
  graph.setPointSizes(maskedSizes)
  graph.setPointColors(maskedColors)
  graph.setPointImageIndices(maskedImageIndices)
  graph.setPointImageSizes(maskedImageSizes)
  applyTeamFilter()
  graph.setConfig({ focusedPointIndex: undefined })
  graph.render()
}

// ── Tooltip ────────────────────────────────────────────────────────────────

let neighborHintShown = false
function renderTooltipHTML(index: number, locked = false): string {
  const node = rawNodes[index]
  if (!node) return ''
  const showHint = locked && !neighborHintShown
  if (showHint) neighborHintShown = true
  const verb = isTouch ? 'tap' : 'click'
  const hint = showHint ? `<div class="tt-hint">${verb} a neighbor to compare · ${verb} again to focus</div>` : ''
  return `
    <div class="tt-name">${nodeLabels[index]}</div>
    <div class="tt-meta">${fullTeamName(node.Team)} · ${node.Games.toLocaleString()} games</div>
    ${hint}
  `
}

function showTooltip(index: number, x: number, y: number, locked = false) {
  const html = renderTooltipHTML(index, locked)
  if (!html) return
  tooltip.innerHTML = html
  tooltip.hidden = false
  positionTooltip(x, y)
}

// Distance from tap to the reported node's screen position, in CSS px.
// Cosmos's click callback uses its cached hoveredPoint, which on touch is
// stale (no mousemove between taps), so we re-validate.
function tapIsOnPoint(index: number, event: MouseEvent): boolean {
  const positions = graph.getPointPositions()
  const [sx, sy] = graph.spaceToScreenPosition([positions[index * 2]!, positions[index * 2 + 1]!])
  const dx = event.clientX - sx
  const dy = event.clientY - sy
  const renderedRadius = graph.spaceToScreenRadius((pointSizes[index] ?? 0) / 2)
  // Generous finger slop so fat-finger taps still register.
  return Math.hypot(dx, dy) <= renderedRadius + 24
}

// On touch, anchor a click-tooltip to the node (via its simulation position)
// rather than the finger location — otherwise the fingertip sits where the
// tooltip pops, making the selected node appear disconnected from the tap.
function tooltipAnchorFor(index: number, event: MouseEvent): [number, number] {
  if (!isTouch) return [event.clientX, event.clientY]
  const positions = graph.getPointPositions()
  const sp = graph.spaceToScreenPosition([positions[index * 2]!, positions[index * 2 + 1]!])
  return [sp[0], sp[1]]
}

function positionTooltip(x: number, y: number) {
  const pad = TOOLTIP_PAD
  const tw = tooltip.offsetWidth
  const th = tooltip.offsetHeight
  const vw = window.innerWidth
  const vh = window.innerHeight
  const rawLeft = x + pad + tw > vw ? x - tw - pad : x + pad
  const yAdjusted = isTouch ? y - th - TOUCH_TOOLTIP_LIFT : y
  const rawTop = yAdjusted + pad + th > vh ? yAdjusted - th - pad : yAdjusted + pad
  tooltip.style.left = Math.max(pad, Math.min(vw - tw - pad, rawLeft)) + 'px'
  tooltip.style.top  = Math.max(pad, Math.min(vh - th - pad, rawTop))  + 'px'
}

// ── Lock (focused node + highlighted neighbors) ────────────────────────────

function resetLockState() {
  lockedIndex = null
  lockedHighlighted.clear()
  tooltip.hidden = true
  exitPairMode()
}

function clearLock() {
  if (lockedIndex === null) return
  resetLockState()
  graph.setLinkColors(activeLinkColors)
  applyTeamFilter()
  graph.setConfig({ focusedPointIndex: undefined })
  graph.render()
}

// ── Pair view (shift+click on a neighbor) ──────────────────────────────────

function enterPairMode(neighbor: number) {
  if (lockedIndex === null || neighbor === lockedIndex) return
  if (!lockedHighlighted.has(neighbor)) return
  const weight = edgeWeights.get(pairKey(lockedIndex, neighbor))
  if (weight === undefined) return
  pairIndex = neighbor
  tooltip.hidden = true
  pairTipA.innerHTML = renderTooltipHTML(lockedIndex)
  pairTipB.innerHTML = renderTooltipHTML(neighbor)
  pairEdgeLabel.innerHTML =
    `<span class="pel-count">${weight.toLocaleString()}</span><span class="pel-suffix">shared games</span>`
  pairTipA.hidden = false
  pairTipB.hidden = false
  pairEdgeLabel.hidden = false
  if (pairRafId === null) pairRafId = requestAnimationFrame(tickPairOverlay)
}

function exitPairMode() {
  if (pairIndex === null) return
  pairIndex = null
  pairTipA.hidden = true
  pairTipB.hidden = true
  pairEdgeLabel.hidden = true
  if (pairRafId !== null) {
    cancelAnimationFrame(pairRafId)
    pairRafId = null
  }
}

function placeTipNearPoint(el: HTMLElement, screenX: number, screenY: number) {
  const pad = TOOLTIP_PAD
  const tw = el.offsetWidth
  const th = el.offsetHeight
  const vw = window.innerWidth
  const vh = window.innerHeight
  const left = screenX + pad + tw > vw ? screenX - tw - pad : screenX + pad
  const top  = screenY - th - pad < 0 ? screenY + pad : screenY - th - pad
  el.style.left = Math.max(pad, Math.min(vw - tw - pad, left)) + 'px'
  el.style.top  = Math.max(pad, Math.min(vh - th - pad, top))  + 'px'
}

function tickPairOverlay() {
  pairRafId = null
  if (pairIndex === null || lockedIndex === null) return
  const positions = graph.getPointPositions()
  const aSpace: [number, number] = [positions[lockedIndex * 2]!, positions[lockedIndex * 2 + 1]!]
  const bSpace: [number, number] = [positions[pairIndex  * 2]!, positions[pairIndex  * 2 + 1]!]
  const a = graph.spaceToScreenPosition(aSpace)
  const b = graph.spaceToScreenPosition(bSpace)
  placeTipNearPoint(pairTipA, a[0], a[1])
  placeTipNearPoint(pairTipB, b[0], b[1])
  pairEdgeLabel.style.left = ((a[0] + b[0]) / 2) + 'px'
  pairEdgeLabel.style.top  = ((a[1] + b[1]) / 2) + 'px'
  pairRafId = requestAnimationFrame(tickPairOverlay)
}

function lockTo(index: number) {
  lockedIndex = index
  selectWithTeamFilter(index)
  highlightLockedLinks()
  graph.setConfig({ focusedPointIndex: index })
}

function highlightLockedLinks() {
  const boosted = new Float32Array(activeLinkColors)
  const edgeCount = activeLinks.length / 2
  for (let i = 0; i < edgeCount; i++) {
    const src = activeLinks[i * 2]
    const tgt = activeLinks[i * 2 + 1]
    boosted[i * 4 + 3] = (src === lockedIndex || tgt === lockedIndex) ? 1 : 0
  }
  graph.setLinkColors(boosted)
  graph.render()
}

// ── Team filter ────────────────────────────────────────────────────────────

function allSelected() { return selectedTeams.size === allTeams.length }

function isTeamVisible(index: number): boolean {
  return allSelected() || selectedTeams.has(rawNodes[index]?.Team ?? '')
}

// True when the node is currently "foreground" — not orphan-filtered, its
// team isn't deselected, and (when something else is locked) it's one of the
// lock's highlighted neighbors. Drives hover tooltip, cursor style, and
// drag-cancellation in one place.
function isInteractive(index: number): boolean {
  if (!connectedSet.has(index)) return false
  if (!isTeamVisible(index)) return false
  if (lockedIndex !== null && !lockedHighlighted.has(index)) return false
  return true
}

function selectWithTeamFilter(index: number) {
  const neighbors = graph.getAdjacentIndices(index) ?? []
  const highlighted = [index, ...neighbors.filter(isTeamVisible)]
  lockedHighlighted = new Set(highlighted)
  graph.selectPointsByIndices(highlighted)
}

function applyTeamFilter() {
  if (allSelected()) {
    graph.unselectPoints()
    return
  }
  const indices = allTeams.flatMap(t => selectedTeams.has(t) ? (indicesByTeam.get(t) ?? []) : [])
  graph.selectPointsByIndices(indices)
}

// ── Graph ──────────────────────────────────────────────────────────────────

const graph = new Graph(graphDiv, {
  backgroundColor: '#08090d',
  spaceSize: 4096,
  // Touch devices use baked positions in nodes.csv; sim is paused shortly
  // after init (see init tail) so older GPUs don't jitter them around.
  // pixelRatio: isTouch ? TOUCH_PIXEL_RATIO : DESKTOP_PIXEL_RATIO,
  simulationDecay: 6000,
  simulationFriction: 0.3,
  simulationGravity: 0.1,
  simulationRepulsion: 3,
  simulationLinkDistance: 220,
  simulationLinkSpring: 1,
  fitViewOnInit: true,
  fitViewDelay: FIT_VIEW_DELAY_MS,
  fitViewPadding: 0.05,
  enableDrag: !isTouch,
  scalePointsOnZoom: true,
  scaleLinksOnZoom: true,
  linkVisibilityDistanceRange: LINK_VIS_DISTANCE_RANGE,
  linkVisibilityMinTransparency: LINK_VIS_MIN_TRANSPARENCY,
  curvedLinks: false,
  pointGreyoutOpacity: POINT_GREYOUT_OPACITY,
  linkGreyoutOpacity: LINK_GREYOUT_OPACITY,
  focusedPointRingColor: '#ffffff',
  onPointMouseOver: (index, _pos, event) => {
    // Track the hovered index unconditionally so the pointerdown capture
    // handler can veto drags on background nodes.
    lastHoveredIndex = index
    // Hover previews don't make sense on touch — pointerover fires on the
    // initial touch, which caused nodes to appear "selected" before the
    // tap even resolved.
    if (isTouch) return
    const interactive = isInteractive(index)
    // Suppress the pointer cursor over background nodes.
    document.body.classList.toggle('graph-hover-invalid', !interactive)
    if (!event) return
    if (!interactive) return
    if (pairIndex !== null) return
    showTooltip(index, event.clientX, event.clientY)
    if (lockedIndex === null) selectWithTeamFilter(index)
  },
  onPointMouseOut: () => {
    lastHoveredIndex = null
    if (isTouch) return
    document.body.classList.remove('graph-hover-invalid')
    tooltip.hidden = true
    if (lockedIndex === null) applyTeamFilter()
  },
  onPointClick: (index, _pos, event) => {
    if (!connectedSet.has(index)) return
    if (!isTeamVisible(index)) return
    // Cosmos dispatches onPointClick based on its last-known hoveredPoint,
    // which is only refreshed by mousemove. On touch there's no mousemove
    // between taps, so a tap on empty space still reports the previously
    // tapped node. Verify the tap actually landed near the node; otherwise
    // treat it as a background click.
    if (isTouch && !tapIsOnPoint(index, event)) {
      clearLock()
      return
    }
    // Greyed non-neighbors while locked act as a deselect — the user almost
    // certainly meant the background.
    if (lockedIndex !== null && !lockedHighlighted.has(index)) {
      clearLock()
      return
    }
    // Tap on the locked node while in pair mode → drop pair, keep lock.
    if (lockedIndex !== null && index === lockedIndex && pairIndex !== null) {
      exitPairMode()
      return
    }
    // Click a neighbor of the locked node to open pair view; click the same
    // neighbor again to switch focus to it.
    if (lockedIndex !== null && index !== lockedIndex && lockedHighlighted.has(index)
        && pairIndex !== index) {
      enterPairMode(index)
      return
    }
    if (pairIndex !== null) exitPairMode()
    const [tx, ty] = tooltipAnchorFor(index, event)
    showTooltip(index, tx, ty, true)
    lockTo(index)
  },
  onBackgroundClick: () => clearLock(),
})

// Capture-phase pointerdown on the graph container: if the pointer is over a
// node whose team is currently deselected, stop the event before cosmos's
// drag behavior (bound on the child canvas) can start tracking it.
graphDiv.addEventListener('pointerdown', (e) => {
  if (lastHoveredIndex === null) return
  if (isInteractive(lastHoveredIndex)) return
  e.stopPropagation()
  e.preventDefault()
}, true)

graph.setConfig({
  onZoomEnd: () => { if (graph.getZoomLevel() < MIN_ZOOM) graph.zoom(MIN_ZOOM, 250) },
})

// ── Keyboard + export ──────────────────────────────────────────────────────

document.addEventListener('keydown', (e) => {
  if (e.code === 'Escape') {
    if (pairIndex !== null) exitPairMode()
    else clearLock()
    closeSearch()
  }
  if (e.code === 'Space' && document.activeElement === document.body) {
    e.preventDefault()
    simToggle.click()
  }
  // Shift+E → export current layout as nodes.csv, for baking positions onto
  // devices that can't converge the force sim.
  if (e.code === 'KeyE' && e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey
      && document.activeElement === document.body) {
    e.preventDefault()
    exportNodesWithLayout()
  }
})

function exportNodesWithLayout() {
  const positions = graph.getPointPositions()
  const rows = rawNodes.map((n, i) => ({
    Id: n.Id,
    Label: n.Label,
    Games: n.Games,
    Team: n.Team,
    x: Math.round(positions[i * 2] ?? 0),
    y: Math.round(positions[i * 2 + 1] ?? 0),
  }))
  downloadText('nodes.csv', Papa.unparse(rows, { newline: '\n' }), 'text/csv;charset=utf-8')
}

function downloadText(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ── Sim + recenter controls ───────────────────────────────────────────────

function onClickAll(targets: HTMLElement[], handler: () => void) {
  for (const el of targets) el.addEventListener('click', handler)
}

onClickAll([recenterBtn, recenterFloat], () => graph.fitView(RECENTER_MS))

function syncSimButtons(paused: boolean) {
  for (const btn of [simToggle, simToggleFloat]) {
    btn.classList.toggle('active', paused)
    btn.setAttribute('aria-pressed', paused ? 'true' : 'false')
    btn.setAttribute('aria-label', paused ? 'Resume simulation' : 'Pause simulation')
  }
}
function toggleSim() {
  if (graph.isSimulationRunning) {
    graph.pause()
    syncSimButtons(true)
  } else {
    graph.unpause()
    syncSimButtons(false)
  }
}
onClickAll([simToggle, simToggleFloat], toggleSim)

// ── Sim settings overlay ───────────────────────────────────────────────────

function openSimSettings()  {
  simSettingsPanel.hidden = false
  simSettingsBtn.classList.add('active')
}
function closeSimSettings() {
  simSettingsPanel.hidden = true
  simSettingsBtn.classList.remove('active')
}
simSettingsBtn.addEventListener('click', () => {
  simSettingsPanel.hidden ? openSimSettings() : closeSimSettings()
})
simCloseBtn.addEventListener('click', closeSimSettings)

// ── Help panel ─────────────────────────────────────────────────────────────

function openHelp() {
  helpPanel.hidden = false
  helpToggle.classList.add('active')
  helpToggle.setAttribute('aria-expanded', 'true')
}
function closeHelp() {
  helpPanel.hidden = true
  helpToggle.classList.remove('active')
  helpToggle.setAttribute('aria-expanded', 'false')
}
helpToggle.addEventListener('click', () => {
  helpPanel.hidden ? openHelp() : closeHelp()
})
helpCloseBtn.addEventListener('click', closeHelp)
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !helpPanel.hidden) closeHelp()
})

// ── Panel toggle ──────────────────────────────────────────────────────────

if (window.matchMedia(MOBILE_BREAKPOINT).matches) {
  document.body.classList.add('collapsed-panel')
  panelToggle.setAttribute('aria-expanded', 'false')
  panelToggle.setAttribute('aria-label', 'Open filters panel')
}
panelToggle.addEventListener('click', () => {
  const nowCollapsed = document.body.classList.toggle('collapsed-panel')
  panelToggle.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true')
  panelToggle.setAttribute('aria-label', nowCollapsed ? 'Open filters panel' : 'Collapse filters panel')
  if (nowCollapsed) closeSimSettings()
})

// ── Sim parameter sliders ──────────────────────────────────────────────────

function simSlider(id: string, valId: string, key: string) {
  const input = document.getElementById(id) as HTMLInputElement
  const val   = document.getElementById(valId)!
  input.addEventListener('input', () => {
    const v = parseFloat(input.value)
    val.textContent = String(parseFloat(v.toFixed(2)))
    graph.setConfig({ [key]: v } as never)
    if (!graph.isSimulationRunning) graph.start(0.1)
  })
}
simSlider('s-repulsion',  'sv-repulsion',  'simulationRepulsion')
simSlider('s-linkdist',   'sv-linkdist',   'simulationLinkDistance')
simSlider('s-linkspring', 'sv-linkspring', 'simulationLinkSpring')
simSlider('s-friction',   'sv-friction',   'simulationFriction')
simSlider('s-gravity',    'sv-gravity',    'simulationGravity')

// ── Filter sliders (edge cutoff + player cutoff) ──────────────────────────

function currentEdge()   { return parseInt(edgeCutoffInput.value,   10) || DEFAULT_FILTER_MIN }
function currentPlayer() { return parseInt(playerCutoffInput.value, 10) || minPlayerGames }

function wireCutoffSlider(
  slider: HTMLInputElement,
  num: HTMLInputElement,
  min: number,
  max: number,
  commit: (v: number) => void,
) {
  slider.min = String(min)
  slider.max = String(max)
  slider.value = String(min)
  num.min = String(min)
  num.max = String(max)
  num.value = String(min)
  slider.addEventListener('input', () => {
    num.value = slider.value
    previewFilters()
  })
  slider.addEventListener('change', () => commit(parseInt(slider.value, 10) || min))
  num.addEventListener('change', () => {
    const v = Math.min(max, Math.max(min, parseInt(num.value, 10) || min))
    num.value = String(v)
    slider.value = String(v)
    commit(v)
  })
}

wireCutoffSlider(edgeCutoffInput,   edgeCutoffVal,   DEFAULT_FILTER_MIN, maxGames,       v => commitFilters(v, currentPlayer()))
wireCutoffSlider(playerCutoffInput, playerCutoffVal, minPlayerGames,     maxPlayerGames, v => commitFilters(currentEdge(), v))

// ── Search ────────────────────────────────────────────────────────────────

function closeSearch() {
  searchResults.hidden = true
  searchResults.innerHTML = ''
  searchInput.value = ''
  searchInput.blur()
}

function selectableItems(): HTMLLIElement[] {
  return [...searchResults.querySelectorAll<HTMLLIElement>('li[data-index]:not([data-hidden="1"])')]
}
function setActiveItem(li: HTMLLIElement | null) {
  searchResults.querySelectorAll('li[aria-selected="true"]').forEach(el => el.removeAttribute('aria-selected'))
  if (li) {
    li.setAttribute('aria-selected', 'true')
    li.scrollIntoView({ block: 'nearest' })
  }
}

searchInput.addEventListener('input', () => {
  const q = searchInput.value.trim().toLowerCase()
  if (!q) { searchResults.hidden = true; searchResults.innerHTML = ''; return }

  const words = q.split(/\s+/)
  const matches = nodeLabels
    .map((label, i) => ({ label, i }))
    .filter(({ label }) => words.every(w => label.toLowerCase().includes(w)))
    .slice(0, MAX_SEARCH_RESULTS)

  if (!matches.length) { searchResults.hidden = true; return }

  searchResults.innerHTML = matches.map(({ label, i }) => {
    const team = rawNodes[i]?.Team ?? ''
    const hidden = !connectedSet.has(i) || !isTeamVisible(i)
    const cls = hidden ? ' class="sr-hidden"' : ''
    const suffix = hidden ? '<span class="sr-tag">filtered</span>' : ''
    return `<li data-index="${i}"${hidden ? ' data-hidden="1"' : ''}${cls}>${label}<span class="sr-team">${team}</span>${suffix}</li>`
  }).join('')
  searchResults.hidden = false
  setActiveItem(selectableItems()[0] ?? null)
})

// mousedown (not click) so we fire before the input's blur handler hides us
searchResults.addEventListener('mousedown', (e) => {
  const li = (e.target as HTMLElement).closest('li')
  if (!li) return
  e.preventDefault()
  if (li.dataset.hidden === '1') return
  selectPlayer(Number(li.dataset.index))
  closeSearch()
})

searchInput.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') { closeSearch(); e.stopPropagation(); return }
  if (searchResults.hidden) return
  const items = selectableItems()
  if (!items.length) return
  const current = searchResults.querySelector<HTMLLIElement>('li[aria-selected="true"]')
  const idx = current ? items.indexOf(current) : -1
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setActiveItem(items[Math.min(items.length - 1, idx + 1)] ?? null)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setActiveItem(items[Math.max(0, idx - 1)] ?? null)
  } else if (e.key === 'Enter' && current) {
    e.preventDefault()
    selectPlayer(Number(current.dataset.index))
    closeSearch()
  }
})

searchInput.addEventListener('blur', () => {
  setTimeout(() => { searchResults.hidden = true }, SEARCH_BLUR_MS)
})

function selectPlayer(index: number) {
  lockTo(index)
  graph.zoomToPointByIndex(index, PLAYER_FOCUS_MS, PLAYER_FOCUS_ZOOM, true)
  showTooltip(
    index,
    window.innerWidth  * SELECT_PLAYER_TIP_X_RATIO,
    window.innerHeight * SELECT_PLAYER_TIP_Y_RATIO,
    true,
  )
}

// ── Team list ──────────────────────────────────────────────────────────────

function setPillState(li: HTMLElement, on: boolean) {
  if (on) {
    li.style.background = li.dataset.color!
    li.style.color = li.dataset.textColor!
    li.style.borderColor = li.dataset.textColor!
  } else {
    li.style.background = ''
    li.style.color = ''
    li.style.borderColor = ''
  }
}

function refreshTeamPills() {
  teamList.querySelectorAll<HTMLElement>('li[data-team]').forEach(li => {
    setPillState(li, selectedTeams.has(li.dataset.team!))
  })
}

// Called after selectedTeams mutates. If a node is locked, clearLock handles
// the graph update (it calls applyTeamFilter internally); otherwise we have
// to apply the team filter and render ourselves.
function onTeamSelectionChanged() {
  refreshTeamPills()
  if (lockedIndex !== null) {
    clearLock()
  } else {
    applyTeamFilter()
    graph.render()
  }
}

teamList.innerHTML = sortedLeagues.map(league => {
  const teams = teamsByLeague.get(league)!
  const rows = teams.map(abr => {
    const { primary, secondary } = teamPalette(abr)
    const years = teamYears(abr)
    const yearSpan = years ? `<span class="team-years">${years}</span>` : ''
    return `<li data-team="${abr}" data-color="${primary}" data-text-color="${secondary}"><span class="team-name">${fullTeamName(abr)}</span>${yearSpan}</li>`
  }).join('')
  return `<li class="league-header">${leagueDisplayName(league)}</li>${rows}`
}).join('')
refreshTeamPills()

teamList.addEventListener('click', (e) => {
  const li = (e.target as HTMLElement).closest('li[data-team]') as HTMLElement | null
  if (!li) return
  const team = li.dataset.team!
  if (selectedTeams.has(team)) selectedTeams.delete(team)
  else selectedTeams.add(team)
  onTeamSelectionChanged()
})

teamsClear.addEventListener('click', () => {
  selectedTeams.clear()
  onTeamSelectionChanged()
})

teamsAll.addEventListener('click', () => {
  allTeams.forEach(t => selectedTeams.add(t))
  onTeamSelectionChanged()
})

// ── Per-team image textures (primary fill + secondary outline) ────────────

function makeTeamImage(primary: string, secondary: string): ImageData {
  const c = document.createElement('canvas')
  c.width = TEAM_IMAGE_RES
  c.height = TEAM_IMAGE_RES
  const ctx = c.getContext('2d')!
  const cx = TEAM_IMAGE_RES / 2
  const strokeW = TEAM_IMAGE_RES * TEAM_STROKE_RATIO
  const radius = cx - strokeW / 2
  ctx.beginPath()
  ctx.arc(cx, cx, radius, 0, Math.PI * 2)
  ctx.fillStyle = primary
  ctx.fill()
  ctx.lineWidth = strokeW
  ctx.strokeStyle = secondary
  ctx.stroke()
  return ctx.getImageData(0, 0, TEAM_IMAGE_RES, TEAM_IMAGE_RES)
}

// Build one ImageData per distinct team present in nodes.csv, plus a default
// fallback and a fully-transparent sentinel used to hide orphan-filtered
// nodes (cosmos appears to clamp image size to a 1-pixel minimum, so the
// outline would otherwise bleed through at size 0).
const imageDataArray: ImageData[] = []
const teamImageIndex = new Map<string, number>()
{
  const fallback = teamPalette('')  // returns FALLBACK_PALETTE
  teamImageIndex.set('', imageDataArray.length)
  imageDataArray.push(makeTeamImage(fallback.primary, fallback.secondary))
  for (const team of allTeams) {
    const pal = teamPalette(team)
    teamImageIndex.set(team, imageDataArray.length)
    imageDataArray.push(makeTeamImage(pal.primary, pal.secondary))
  }
}
const hiddenImageIndex = imageDataArray.length
{
  // Fully transparent 128x128 — canvas starts transparent, don't draw anything.
  const c = document.createElement('canvas')
  c.width = TEAM_IMAGE_RES
  c.height = TEAM_IMAGE_RES
  const ctx = c.getContext('2d')!
  imageDataArray.push(ctx.getImageData(0, 0, TEAM_IMAGE_RES, TEAM_IMAGE_RES))
}

const pointImageIndices = new Float32Array(rawNodes.length)
rawNodes.forEach((n, i) => {
  pointImageIndices[i] = teamImageIndex.get(n.Team) ?? 0
})

// ── Initial render ─────────────────────────────────────────────────────────

rebuildActive(DEFAULT_FILTER_MIN, minPlayerGames)
graph.setPointPositions(pointPositions)
graph.setPointSizes(pointSizes)
graph.setPointColors(pointColors)
graph.setImageData(imageDataArray)
graph.setPointImageIndices(pointImageIndices)
graph.setPointImageSizes(pointImageSizes)
graph.setLinks(activeLinks)
graph.setLinkColors(activeLinkColors)
graph.render()

document.body.classList.add('loaded')

// On touch, freeze the layout almost immediately after init. nodes.csv ships
// baked positions, so there's nothing for the sim to refine, and older GPUs
// would only add jitter. User can resume manually via the pause button.
if (isTouch) {
  setTimeout(() => {
    if (graph.isSimulationRunning) {
      graph.pause()
      syncSimButtons(true)
    }
  }, TOUCH_PAUSE_DELAY_MS)
}
